import { State } from 'xstate';
import {
  isLegacyOverlayConvertAble,
  TextOverlayV2,
  validateCompatibleLegacyConfig,
  validateOverlayV2Integrity,
} from 'blocks/TextOverlayModal/v2';
import {
  AUTOFRAMED_CAPTIONS_INFOBOX_MESSAGE_CREATE,
  DYNAMIC_VIDEO_AUTOFRAME_INFOBOX_MESSAGE,
} from 'constants/autoframing';
import {
  createMap,
  DeepImmutableMap,
  IEmbedConfig,
  Size,
  Soundwave,
} from 'types';
import bem from 'utils/bem';
import { isMap } from 'utils/immutable';
import { getRatio } from 'utils/numbers';
import {
  DEFAULT_SOUNDWAVE_STATE,
  DYNAMIC_VIDEO_AUTOFRAME_TOOLTIP_MESSAGE,
} from './constants';
import { VideoEditorStateContext } from './context';
import { DYNAMIC_ASSET_VALUE } from './integrations/useDynamicPodcastTextIntegration';
import {
  hasAutoframedDynamicVideo,
  isCaptionsViewOpen,
  isSelectedDynamicAutoframedMediaLayer,
  VideoTemplateStateContent,
} from './state';
import { NavigationMachineContextType } from './state/fsm/navigation-machine';
import { TextOverlayExport } from './state/template/export/types';
import { TextIntegrationId } from './types/core';
import { NavigationMachineEvent } from './types/navigation-actions';

export const block = bem('video-template-editor');
export const previewBlock = bem('video-template-editor-preview');
export const optionTileBlock = bem('video-template-editor-option');
export const optionsTemplateControls = bem(
  'video-template-editor-options-template-controls',
);
export const optionsBlock = bem('video-template-editor-options');
export const canvasBlock = bem('video-template-editor-canvas');
export const waveformViewBlock = bem('waveform-child-view');
export const imageViewBlock = bem('image-child-view');
export const progressViewBlock = bem('progress-child-view');
export const captionsViewBlock = bem('captions-child-view');
export const rndBlock = bem('video-template-editor-rnd');
export const ustPaneBlock = bem('video-template-editor-ust');
export const tileBlock = bem('video-template-editor-tile');
export const gridBlock = bem('video-template-editor-grid');
export const popperTileBlock = bem('popper-tile');
export const saveTemplatePopper = bem('save-template-popper');
export const progressPlacementTileBlock = bem('progress-placement-tile');
export const waveformPlacementPopperBlock = bem('waveform-placement-popper');
export const waveformSettingsTileBlock = bem('waveform-settings-tile');
export const textViewBlock = bem('text-child-view');
export const selectTileBlock = bem('video-template-editor-select-tile');
export const childViewCardBlock = bem('child-view-card');
export const textAssetModalBlock = bem('text-asset-modal');
export const timerPopperBlock = bem('timer-popper');
export const editorCanvasInfoboxBlock = bem('editor-canvas-infobox');
export const addImageModalBlock = bem('add-image-modal');
export const deleteDynamicImageModalBlock = bem('delete-dynamic-image-modal');
export const addImageTypeModalBlock = bem('add-image-type-modal');
export const addTextTypeModalBlock = bem('add-text-type-modal');
export const toggleCardBlock = bem('video-template-editor-toggle-card');
export const introOutroChildViewBlock = bem('intro-outro-child-view');
export const editorModalPreviewBlock = bem(
  'video-template-editor-modal-preview',
);

export const DEFAULT_END_MILLI = 10000;

export const DEFAULT_START_END_MILLI = {
  startMilli: 0,
  endMilli: DEFAULT_END_MILLI,
};

export const CONFIGURATION_PROPS_FOR_START_END_INIT_KEYS: Array<keyof IEmbedConfig> = [
  'soundwave',
  'slideshowInfo',
  'textOverlayInfo',
  'videoClips',
];

export const colorPickerSwatchClassName = (disabled?: boolean) =>
  block('color-picker-swatch', { disabled });

export function getCanvasDimensions(
  containerDimensions: Size<number>,
  canvasAspectRatio: number,
) {
  if (containerDimensions === undefined || canvasAspectRatio === undefined)
    return [];

  const {
    height: containerHeight,
    width: containerWidth,
  } = containerDimensions;
  const [canvasWidth, canvasHeight] = getRatio(canvasAspectRatio);

  const scale = Math.min(
    containerHeight / canvasHeight,
    containerWidth / canvasWidth,
  );

  return [canvasWidth * scale, canvasHeight * scale];
}

export function roundDimensions(dims: Size<number>) {
  if (!dims) return undefined;

  return {
    height: Math.round(dims.height),
    width: Math.round(dims.width),
  };
}

export function viewportToPct(value: any) {
  if (typeof value === 'string') {
    const units = value.slice(-2).toLowerCase();
    if (units !== 'vw' && units !== 'vh') return value;
    return `${parseFloat(value)}%`;
  }

  if (Object.prototype.toString.call(value) === '[object Object]') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = viewportToPct(value[key]);
      return acc;
    }, {} as React.CSSProperties);
  }

  return value;
}

export function getObj(obj: { [k: string]: any }, ...path: string[]) {
  return path.reduce((current, seg) => {
    const val = current[seg];
    if (val) {
      return val;
    }
    current[seg] = {};
    return current[seg];
  }, obj);
}

/**
As UCS doesn't have time concept, this function allows the initialization
of the start and end time of the properties that need those values in order
to work properly. 
*/
export function initializeStartEndMilli(
  configuration: IEmbedConfig,
  prop: keyof IEmbedConfig,
) {
  const configProp = configuration[prop];
  if (!configProp) {
    return undefined;
  }

  if (Array.isArray(configProp)) {
    return configProp.map(current => ({
      ...current,
      ...DEFAULT_START_END_MILLI,
    }));
  }

  return Object.assign(configProp, { ...DEFAULT_START_END_MILLI });
}

/**
 * Replaces the text overlays with integration data with the default
 * dynamic assets values. This allows templates saved within UCS to be
 * used in other places by taking advantage of the dynamic assets
 * capabilities, like automatically replacing values and using dynamic
 * podcast inputs (podcast name and episode title).
 */
export function formatDynamicTextOverlays(
  textOverlays: TextOverlayExport,
): TextOverlayExport {
  if (!textOverlays) {
    return null;
  }

  return textOverlays.map(textOverlay => {
    const { integrationData } = textOverlay;

    if (!integrationData || integrationData.id === TextIntegrationId.STATIC)
      return textOverlay;

    const valueToReplace = DYNAMIC_ASSET_VALUE[integrationData.type];

    return {
      ...textOverlay,
      text: valueToReplace,
      textHtml: textOverlay.textHtml.replace(textOverlay.text, valueToReplace),
    };
  });
}

/**
 * Encapsulates the logic for validating if the default text asset modal
 * to show for a given overlay should default to the legacy modal and if
 * it should show the v2 compatibility message.
 * @param {TextOverlay | TextOverlayV2} textOverlay current text overlay to check
 * @returns {{ isLegacy: boolean, isV2CompatibilityMode: boolean}} Object indicating if the overlay should be handled by the legacy text editor and if the compatibility message should be shown.
 */
export const getTextOverlayEditorConfig = (
  textOverlay: TextOverlayV2,
): {
  isLegacy: boolean;
  isV2CompatibilityMode: boolean;
} => {
  const isV2Compatible = validateCompatibleLegacyConfig(textOverlay);
  const isV2CompatibilityMode =
    !validateOverlayV2Integrity(textOverlay) &&
    isLegacyOverlayConvertAble(textOverlay);
  const isV2 = validateOverlayV2Integrity(textOverlay);
  const isLegacy = !isV2Compatible || (!isV2 && !isV2CompatibilityMode);

  return {
    isLegacy,
    isV2CompatibilityMode,
  };
};

export function getEditorCanvasInfoBoxMessage(
  state: VideoTemplateStateContent,
  editorState: VideoEditorStateContext,
  navState: State<NavigationMachineContextType, NavigationMachineEvent>,
) {
  // Priority 1: Captions warning (more specific context)
  if (isCaptionsViewOpen(navState) && hasAutoframedDynamicVideo(state)) {
    return AUTOFRAMED_CAPTIONS_INFOBOX_MESSAGE_CREATE;
  }

  // Priority 2: Autoframe video warning
  if (isSelectedDynamicAutoframedMediaLayer(state, editorState)) {
    return DYNAMIC_VIDEO_AUTOFRAME_INFOBOX_MESSAGE;
  }

  return undefined;
}

export function getEditorCanvasTooltipMessage(
  state: VideoTemplateStateContent,
  editorState: VideoEditorStateContext,
) {
  if (isSelectedDynamicAutoframedMediaLayer(state, editorState)) {
    return DYNAMIC_VIDEO_AUTOFRAME_TOOLTIP_MESSAGE;
  }

  return undefined;
}

export function createDefaultSoundwave(
  waveform:
    | DeepImmutableMap<Partial<Soundwave>>
    | Partial<Soundwave> = createMap({}),
) {
  return {
    ...DEFAULT_SOUNDWAVE_STATE,
    ...(isMap(waveform) ? waveform.toJS() : waveform),
  };
}
